/**
 * mapCanvas.ts
 * Построение элементов квадратов для расположения на карте.
 **/


import {SQUARE_CONFIG} from "@/components/modules/MainMap/utils/mapConstants";
import {getCenterPoint, getMinZoomForCircle, getRegionColor} from "@/components/modules/MainMap/utils/mapCalculation";
import {Fill, Style, Icon, Text} from "ol/style";
import Point from "ol/geom/Point";
import {getArea} from "ol/extent";
import type {FeatureLike} from "ol/Feature";
import OLMap from "ol/Map";
import {ComputedRef} from "vue";


/**
 * Проверяет, находится ли точка внутри квадрата
 */
export const isPointInSquare = (
    pixel: number[],
    centerPixel: number[],
    squareHalfSize: number = SQUARE_CONFIG.hitTolerance
): boolean => {
    const dx = pixel[0] - centerPixel[0];
    const dy = pixel[1] - centerPixel[1];
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= squareHalfSize;
};

/**
 * Рисует скругленный квадрат на canvas
 */
const drawRoundedSquare = (
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
): void => {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.arcTo(x + width, y, x + width, y + radius, radius);
    context.lineTo(x + width, y + height - radius);
    context.arcTo(x + width, y + height, x + width - radius, y + height, radius);
    context.lineTo(x + radius, y + height);
    context.arcTo(x, y + height, x, y + height - radius, radius);
    context.lineTo(x, y + radius);
    context.arcTo(x, y, x + radius, y, radius);
    context.closePath();
};

/**
 * Создает canvas с квадратом данных
 */
const createDataSquareCanvas = (
    absoluteValue: number,
    percentValue: number
): HTMLCanvasElement => {
    const squareColor = getRegionColor(percentValue, true);

    const canvas = document.createElement("canvas");
    canvas.width = SQUARE_CONFIG.size;
    canvas.height = SQUARE_CONFIG.size;
    const context = canvas.getContext("2d");

    if (!context) return canvas;

    const centerX = SQUARE_CONFIG.size / 2;
    const centerY = SQUARE_CONFIG.size / 2;
    const halfSize = SQUARE_CONFIG.squareSize / 2;

    // Настройка тени
    context.shadowColor = SQUARE_CONFIG.shadowColor;
    context.shadowBlur = SQUARE_CONFIG.shadowBlur;
    context.shadowOffsetX = SQUARE_CONFIG.shadowOffsetX;
    context.shadowOffsetY = SQUARE_CONFIG.shadowOffsetY;

    // Рисуем скругленный квадрат
    const x = centerX - halfSize;
    const y = centerY - halfSize;
    drawRoundedSquare(
        context,
        x,
        y,
        SQUARE_CONFIG.squareSize,
        SQUARE_CONFIG.squareSize,
        SQUARE_CONFIG.borderRadius
    );

    context.fillStyle = squareColor;
    context.fill();

    // Убираем тень для обводки
    context.shadowColor = "transparent";
    context.strokeStyle = SQUARE_CONFIG.strokeColor;
    context.lineWidth = SQUARE_CONFIG.strokeWidth;
    context.stroke();

    // Рисуем текст
    context.fillStyle = SQUARE_CONFIG.textColor;
    context.textAlign = "center";
    context.textBaseline = "middle";

    // Первая строка: абсолютное значение
    context.font = SQUARE_CONFIG.boldFont;
    context.fillText(absoluteValue.toString(), centerX, centerY - 7);

    // Вторая строка: процент
    const percentText = percentValue >= 0
        ? `+${percentValue.toFixed(1)}%`
        : `${percentValue.toFixed(1)}%`;
    context.font = SQUARE_CONFIG.regularFont;
    context.fillText(percentText, centerX, centerY + 8);

    return canvas;
};

/**
 * Создает стили для квадрата с данными и подписью
 */
export const createDataSquareStyle = (
    centerPoint: Point,
    absoluteValue: number,
    percentValue: number,
    labelText: string,
    labelFontSize: "500 16px Roboto" | "600 16px Roboto" | "500 14px Roboto" | "600 12px Roboto" |  "600 14px Roboto",
    labelOffsetY: number
): Style[] => {
    const canvas = createDataSquareCanvas(absoluteValue, percentValue);

    // Стиль квадрата
    const pointStyle = new Style({
        geometry: centerPoint,
        image: new Icon({
            img: canvas,
            size: [SQUARE_CONFIG.size, SQUARE_CONFIG.size],
            anchor: [0.5, 0.52]
        })
    });

    // Стиль подписи
    const labelStyle = new Style({
        geometry: centerPoint,
        text: new Text({
            text: labelText,
            font: labelFontSize,
            offsetY: labelOffsetY,
            fill: new Fill({ color: "#373D3F" }),
            backgroundFill: new Fill({ color: "#FFFFFFB2" }),
            padding: labelFontSize.includes("16px") ? [2, 6, 2, 6] : [1, 4, 1, 4]
        })
    });

    return [pointStyle, labelStyle];
};

/**
 * Проверяет, находится ли курсор над квадратом
 */
export const isHoveringSquare = (
    feature: FeatureLike,
    pixel: number[],
    isDistrictView: boolean,
    map: OLMap,
    regionDataMap: ComputedRef,
    districtDataMap: ComputedRef
): boolean => {
    if (!map) return false;

    const featureId = feature.get("id");
    if (!featureId) return false;

    const featureKey = featureId.toLowerCase();

    if (isDistrictView) {
        const districtData = districtDataMap.value.get(featureKey);
        if (!districtData) return false;

        const centerPoint = getCenterPoint(featureKey, null, true);
        if (!centerPoint) return false;

        const centerCoords = centerPoint.getCoordinates();
        const centerPixel = map.getPixelFromCoordinate(centerCoords);

        return isPointInSquare(pixel, centerPixel);
    } else {
        const regionData = regionDataMap.value.get(featureKey);
        if (!regionData) return false;

        const geometry = feature.getGeometry();
        if (!geometry) return false;

        const extent = geometry.getExtent();
        const regionArea = getArea(extent);
        const minZoomForCircle = getMinZoomForCircle(regionArea);
        const currentZoom = map.getView().getZoom() || 3;

        if (currentZoom >= minZoomForCircle) {
            const centerPoint = getCenterPoint(featureKey, extent, false);
            if (!centerPoint) return false;

            const centerCoords = centerPoint.getCoordinates();
            const centerPixel = map.getPixelFromCoordinate(centerCoords);

            return isPointInSquare(pixel, centerPixel);
        }
    }

    return false;
};